import React, { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import { FormattedDate } from 'react-intl';
import CardInformation from '@tovia/man-ui/lib/components/Card/CardInformation';
import Card from '@tovia/man-ui/lib/components/Card/Card';
import LoaderWrapper from '../../../components/Loader/LoaderWrapper';
import { load } from '../../../redux/modules/potd';
import { formatDateDefaultOptions } from '../../../utils/converters';
import GridCards from '../../Cards/Grid/GridCards';
import Helmet from 'react-helmet';
import { useSelector } from '../../../redux/modules/helpers/useSelector';
import { MetArt } from 'src/client/constants/sites';
import { useShowSfwSite } from 'src/client/helpers/useFeatureFlags';
import CardMedia from 'src/client/components/ui/Card/CardMedia';

interface Props {
  // eslint-disable-next-line react/require-default-props
  className?: string;
}

export default function PotdArchive(props: Props) {
  const { className = '' } = props;
  const site = useSelector((state) => state.site);
  const contentCdnUrl = useSelector((state) => state.app.contentCdnUrl);
  const {
    items: { dates: potdDates },
    loading,
  } = useSelector((state) => state.potd);

  const dispatch = useDispatch();
  const isBlurred = useShowSfwSite();

  const loadData = useCallback(() => {
    dispatch(load());
  }, [dispatch]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const generateImgSrc = useCallback(
    (date) => `${contentCdnUrl}/${site.UUID}/media/potd/${moment(date).format('YYYYMMDD')}.jpg`,
    [contentCdnUrl, site],
  );

  const renderPotdImages = () =>
    potdDates.map((date) => {
      const imgSrc = generateImgSrc(date);
      return (
        <a
          className="grid-card-wrapper"
          key={date}
          {...(!isBlurred ? { href: imgSrc, target: '_blank', rel: 'noopener noreferrer' } : {})}
        >
          <Card>
            <CardMedia blurred={isBlurred} imgSrc={imgSrc} altText={date} />
            <CardInformation
              title={<FormattedDate value={new Date(date)} {...formatDateDefaultOptions} month="short" />}
            />
          </Card>
        </a>
      );
    });

  const showLoader = loading;
  const potdImagesMarkup = potdDates && potdDates.length ? <GridCards>{renderPotdImages()}</GridCards> : null;

  return (
    <div className={className}>
      <Helmet>
        <title>
          {/* SEO_CLEANUP */}
          {site.abbreviation === MetArt.abbreviation
            ? 'MetArt Archives | Browse content day by day here'
            : `Photo of the Day Archive - ${site.name}`}
        </title>
        <meta property="og:url" content={`https://www.${site.domain}/photo-archive`} />
        <meta property="prerender:ttl" content="86400" />
      </Helmet>
      {showLoader && <LoaderWrapper className="imageViewer-loader" reloadFunction={loadData} />}
      {potdImagesMarkup}
    </div>
  );
}
